import { useEffect, useState, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getPortfolioAnalytics } from '@/lib/api'
import type { PortfolioAnalytics } from '@/lib/types'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ReferenceLine,
} from 'recharts'
import { TrendingUp, TrendingDown, RefreshCw, BarChart2, Award, AlertTriangle } from 'lucide-react'
import PageHeader from '@/components/ui/PageHeader'
import { clsx } from 'clsx'

function fmt(n: number | null | undefined) { if (n == null || !Number.isFinite(n)) return '£0'; return `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}` }

function KpiCard({ label, value, sub, positive, hero }: {
  label: string; value: string; sub?: string; positive?: boolean; hero?: boolean
}) {
  return (
    <div className="stat-card">
      <p className="stat-label">{label}</p>
      <p className={clsx(
        hero ? 'stat-hero' : 'stat-value text-xl',
        positive === true ? 'text-emerald' : positive === false ? 'text-rose-light' : 'text-text-primary',
      )}>
        {value}
      </p>
      {sub && <p className="text-xs text-text-muted">{sub}</p>}
    </div>
  )
}

function MonthlyHeatmap({ data }: { data: PortfolioAnalytics['monthly_returns'] }) {
  return (
    <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5">
      {[...data].reverse().map(m => {
        const pos = m.return_pct >= 0
        const intensity = Math.min(Math.abs(m.return_pct) / 5, 1) // cap at 5%
        return (
          <div
            key={m.month}
            title={`${m.month}: ${(m.return_pct ?? 0) > 0 ? '+' : ''}${(m.return_pct ?? 0).toFixed(1)}%`}
            className="aspect-square rounded flex items-center justify-center text-[9px] font-bold cursor-default"
            style={{
              backgroundColor: pos
                ? `rgba(16,185,129,${0.1 + intensity * 0.7})`
                : `rgba(244,63,94,${0.1 + intensity * 0.7})`,
              color: intensity > 0.5 ? '#fff' : (pos ? '#10b981' : '#f43f5e'),
            }}
            aria-label={`${m.month}: ${(m.return_pct ?? 0).toFixed(1)}%`}
          >
            {(m.return_pct ?? 0) > 0 ? '+' : ''}{(m.return_pct ?? 0).toFixed(1)}
          </div>
        )
      })}
    </div>
  )
}

// Lazy tab content re-homed from the former standalone /app/look-through page
const LookThroughTab = lazy(() => import('./LookThroughPage'))

type AnalyticsTab = 'performance' | 'look-through'

const ANALYTICS_TABS: { key: AnalyticsTab; label: string }[] = [
  { key: 'performance',  label: 'Performance' },
  { key: 'look-through', label: 'Look-Through' },
]

export default function AnalyticsPage() {
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<AnalyticsTab>(() => {
    const t = searchParams.get('tab')
    return ANALYTICS_TABS.some(x => x.key === t) ? (t as AnalyticsTab) : 'performance'
  })
  const [data, setData]       = useState<PortfolioAnalytics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)

  const fetch = async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await getPortfolioAnalytics())
    } catch {
      setError('Failed to load analytics data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetch() }, []) // eslint-disable-line

  if (loading && !data && tab === 'performance') {
    return (
      <div className="space-y-6 p-1">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton h-24" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="skeleton h-64" />
          <div className="skeleton h-64" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Portfolio Analytics"
        subtitle="Performance attribution and rolling metrics"
        actions={
          <button onClick={fetch} disabled={loading} className="btn-secondary gap-1.5 text-xs">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
            Refresh
          </button>
        }
      />

      {/* Tabs */}
      <div className="glass rounded-xl p-1 flex items-center gap-1 flex-wrap">
        {ANALYTICS_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={
              tab === t.key
                ? 'px-4 py-2 rounded-lg text-sm font-medium bg-blue/20 text-blue-light'
                : 'px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Look-Through tab (ex-/app/look-through page, content unchanged) */}
      {tab === 'look-through' && (
        <Suspense fallback={<div className="space-y-4"><div className="skeleton h-24" /><div className="skeleton h-64" /></div>}>
          <LookThroughTab />
        </Suspense>
      )}

      {tab === 'performance' && error && (
        <div className="card p-4 border-rose/20 bg-rose/5 text-sm text-rose-light">{error}</div>
      )}

      {tab === 'performance' && data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
            <KpiCard label="Total Return"    value={`${(data.total_return_pct ?? 0) > 0 ? '+' : ''}${(data.total_return_pct ?? 0).toFixed(1)}%`} positive={(data.total_return_pct ?? 0) >= 0} hero />
            <KpiCard label="Annualised"      value={`${(data.annualised_return_pct ?? 0).toFixed(1)}%`} positive={(data.annualised_return_pct ?? 0) >= 0} sub="Projected" />
            <KpiCard label="YTD Return"      value={`${(data.ytd_return_pct ?? 0) > 0 ? '+' : ''}${(data.ytd_return_pct ?? 0).toFixed(1)}%`} positive={(data.ytd_return_pct ?? 0) >= 0} sub="Since Jan 2026" />
            <KpiCard label="Best Month"      value={`+${(data.best_month_pct ?? 0).toFixed(1)}%`} positive={true} />
            <KpiCard label="Worst Month"     value={`${(data.worst_month_pct ?? 0).toFixed(1)}%`} positive={false} />
          </div>

          {/* Charts row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Sector attribution */}
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart2 size={15} className="text-text-muted" aria-hidden />
                <h2 className="panel-title">Sector Attribution</h2>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.attribution} layout="vertical" margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                  <XAxis type="number" tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="sector" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={90} />
                  <Tooltip
                    formatter={(v: number, name: string) => [`${v.toFixed(2)}%`, name === 'contribution_pct' ? 'Contribution' : 'Weight']}
                    contentStyle={{ background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--text-primary))', borderRadius: 8 }}
                  />
                  <Bar dataKey="weight_pct"        fill="#3b82f620" stackId="a" radius={[0,0,0,0]} />
                  <Bar dataKey="contribution_pct"  fill="#10b981"   stackId="b" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue/30" />Weight</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald" />Contribution</span>
              </div>
            </div>

            {/* Rolling Sharpe */}
            <div className="card p-5">
              <h2 className="panel-title mb-4">Rolling 30-Day Sharpe Ratio</h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.rolling_metrics} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} interval={6}
                    tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={32} domain={['auto','auto']} />
                  <ReferenceLine y={1} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'Target', fill: '#f59e0b', fontSize: 9 }} />
                  <Tooltip
                    formatter={(v: number) => [v.toFixed(2), 'Sharpe']}
                    contentStyle={{ background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--text-primary))', borderRadius: 8 }}
                  />
                  <Line type="monotone" dataKey="sharpe" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly returns heatmap */}
          <div className="card p-5">
            <h2 className="panel-title mb-4">Monthly Returns (last 12 months)</h2>
            <MonthlyHeatmap data={data.monthly_returns} />
            <div className="flex items-center gap-4 mt-3 text-xs text-text-muted">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(16,185,129,0.7)' }} />
                Positive month
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(244,63,94,0.7)' }} />
                Negative month
              </span>
              <span className="text-text-muted ml-auto">Hover each cell for exact value</span>
            </div>
          </div>

          {/* Top / Bottom performers */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Award size={15} className="text-emerald" aria-hidden />
                <h2 className="panel-title">Top Performers</h2>
              </div>
              <ul className="space-y-3" aria-label="Top performing holdings">
                {(data.top_performers ?? []).map((p, i) => (
                  <li key={p.symbol} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-emerald/10 text-emerald text-xs font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{p.name}</p>
                      <p className="text-xs text-text-muted font-mono">{p.symbol} · {fmt(p.value)}</p>
                    </div>
                    <span className="delta-chip-pos shrink-0">
                      <TrendingUp size={12} aria-hidden />
                      +{(p.return_pct ?? 0).toFixed(1)}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle size={15} className="text-amber" aria-hidden />
                <h2 className="panel-title">Underperformers</h2>
              </div>
              <ul className="space-y-3" aria-label="Underperforming holdings">
                {(data.bottom_performers ?? []).map((p, i) => (
                  <li key={p.symbol} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-rose/10 text-rose-light text-xs font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{p.name}</p>
                      <p className="text-xs text-text-muted font-mono">{p.symbol} · {fmt(p.value)}</p>
                    </div>
                    <span className="delta-chip-neg shrink-0">
                      <TrendingDown size={12} aria-hidden />
                      {(p.return_pct ?? 0).toFixed(1)}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
